/**
 * Stock Quote Hooks
 * Fetch real-time stock prices with TanStack Query
 */

import { useQuery } from '@tanstack/react-query'
import {
  getStockQuote,
  getMultipleQuotes,
  type StockQuote,
} from '@/lib/services/stockPrice'

// Query keys
export const STOCK_QUOTE_KEY = (symbol: string) => [
  'stock-quote',
  symbol.toUpperCase(),
]
export const STOCK_QUOTES_KEY = (symbols: string[]) => [
  'stock-quotes',
  symbols.sort().join(','),
]

/**
 * Hook to fetch a single stock quote
 */
export function useStockQuote(symbol: string | undefined | null) {
  return useQuery({
    queryKey: STOCK_QUOTE_KEY(symbol || ''),
    queryFn: () => getStockQuote(symbol!),
    enabled: !!symbol,
    staleTime: 60 * 1000, // 1 minute (market data refreshes frequently)
    refetchInterval: 60 * 1000, // Refetch every minute
  })
}

/**
 * Hook to fetch multiple stock quotes
 * Returns a Map keyed by symbol
 */
export function useStockQuotes(symbols: string[]) {
  const filteredSymbols = symbols.filter(Boolean)

  return useQuery<Map<string, StockQuote>>({
    queryKey: STOCK_QUOTES_KEY(filteredSymbols),
    queryFn: () => getMultipleQuotes(filteredSymbols),
    enabled: filteredSymbols.length > 0,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  })
}
